import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { getDoc, doc, setDoc, updateDoc, deleteDoc, serverTimestamp  } from "firebase/firestore";
import { db } from '../firebase/config';
import { AuthContext } from '../context/AuthContext';
import { ShoppingBag, Star, ChevronLeft, ChevronRight, Edit, Save, X, Heart } from 'lucide-react';
import Footer from "../components/Footer";
import FeaturedProducts from "../components/FeaturedProducts";
import './Product.css';

export default function Product() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // New state for edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editedProduct, setEditedProduct] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if the current user is an admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult();
          setIsAdmin(!!idTokenResult.claims.admin);
        } catch (error) {
          console.error("Error checking admin status:", error);
          setIsAdmin(false);
        }
      }
    };
    checkAdminStatus();
  }, [user]);

    // Check if product is already saved when product loads
    useEffect(() => {
      const checkSavedStatus = async () => {
        if (user && product) {
          try {
            const savedProductRef = doc(
              db, 
              'savedProducts', 
              user.uid, 
              'items', 
              product.id
            );
            const savedProductSnapshot = await getDoc(savedProductRef);
            setIsSaved(savedProductSnapshot.exists());
          } catch (error) {
            console.error('Error checking saved status:', error);
          }
        }
      };
      checkSavedStatus();
    }, [user, product]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const productDoc = await getDoc(doc(db, 'products', productId));
        if (productDoc.exists()) {
          const productData = { id: productDoc.id, ...productDoc.data() };
          setProduct(productData);
          setEditedProduct(productData);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const addToCart = async () => {
    if (!user) {
      alert("Please log in to add items to your bag.");
      return;
    }

    const bagItemRef = doc(db, 'myBag', user.uid, 'items', product.id);
    await setDoc(bagItemRef, { ...product, quantity: 1 });
    
    const successMsg = document.getElementById('success-message');
    successMsg.classList.remove('hidden');
    setTimeout(() => {
      successMsg.classList.add('hidden');
    }, 3000);
  };

  // Image slider handlers (previous implementation)
  const handlePrevImage = () => {
    const imageElement = document.querySelector(".product-image");
    imageElement.classList.add("fade-out");
    
    setTimeout(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
      );
      imageElement.classList.remove("fade-out");
    }, 500);
  };
  
  const handleNextImage = () => {
    const imageElement = document.querySelector(".product-image");
    imageElement.classList.add("fade-out");
    
    setTimeout(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
      );
      imageElement.classList.remove("fade-out");
    }, 500);
  };

  // Admin edit handlers
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedProduct(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) : value
    }));
  };

  const handleSaveProduct = async () => {
    if (!isAdmin) {
      alert("You do not have permission to edit products.");
      return;
    }

    try {
      // Update the product in Firestore
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, editedProduct);
      
      // Update local state
      setProduct(editedProduct);
      setIsEditing(false);
      
      alert("Product updated successfully!");
    } catch (error) {
      console.error("Error updating product:", error);
      alert("Failed to update product. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setEditedProduct(product);
    setIsEditing(false);
  };

    // Add save/unsave product handler
    const handleSaveItem = async () => {
      if (!user) {
        alert('Please log in or sign up to save products');
        return;
      }
  
      try {
        const savedProductRef = doc(
          db, 
          'savedProducts', 
          user.uid, 
          'items', 
          product.id
        );
  
        if (isSaved) {
          // Unsave the product
          await deleteDoc(savedProductRef);
          setIsSaved(false);
          alert('Product removed from saved items');
        } else {
          // Save the product
          await setDoc(savedProductRef, {
            ...product,
            savedAt: serverTimestamp()
          });
          setIsSaved(true);
          alert('Product saved successfully');
        }
      } catch (error) {
        console.error('Error saving/unsaving product:', error);
        alert('Failed to save/unsave product. Please try again.');
      }
    };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-not-found">
        <div className="text-center">
          <h2>Product not found</h2>
          <p>The product you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="product-page">
        <div className="product-container">
          <div className="product-layout">
            {/* Left column with image slider */}
            <div className="product-image-slider">
              <ChevronLeft className="slider-icon left-icon" onClick={handlePrevImage} />
              <img
                src={product.images && product.images.length > 0 
                  ? product.images[currentImageIndex] 
                  : '/path/to/fallback-image.jpg'}
                alt={product.name}
                className="product-image"
              />
              <ChevronRight className="slider-icon right-icon" onClick={handleNextImage} />
            </div>

            {/* Right column */}
            <div className="product-details">
              {/* Admin Edit Toggle */}
              {isAdmin && (
                <div className="admin-edit-toggle">
                  {!isEditing ? (
                    <Edit 
                      className="edit-icon" 
                      onClick={handleEditToggle} 
                      title="Edit Product"
                    />
                  ) : (
                    <div className="edit-actions">
                      <Save 
                        className="save-icon" 
                        onClick={handleSaveProduct} 
                        title="Save Changes"
                      />
                      <X 
                        className="cancel-icon" 
                        onClick={handleCancelEdit} 
                        title="Cancel Editing"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Product Name */}
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={editedProduct.name}
                  onChange={handleInputChange}
                  className="edit-input product-name-input"
                />
              ) : (
                <h1>{product.name}</h1>
              )}
              
              {/* Rating Section */}
              <div className="rating-section">
                <div className="stars">
                  {[0, 1, 2, 3, 4].map((rating) => (
                    <Star
                      key={rating}
                      className={rating < Math.floor(product.rating || 0) ? 'filled-star' : 'empty-star'}
                    />
                  ))}
                </div>
                <p className="reviews-count">
                  {product.reviewCount || 0} reviews
                </p>
              </div>

              {/* Price */}
              <div className="product-price">
                {isEditing ? (
                  <input
                    type="number"
                    name="price"
                    value={editedProduct.price}
                    onChange={handleInputChange}
                    className="edit-input price-input"
                    step="0.01"
                    min="0"
                  />
                ) : (
                  <p>&#8358;{product.price?.toFixed(2)}</p>
                )}
              </div>
              
              {/* Description */}
              <div className="product-description">
                {isEditing ? (
                  <textarea
                    name="description"
                    value={editedProduct.description}
                    onChange={handleInputChange}
                    className="edit-input description-input"
                  />
                ) : (
                  <p>{product.description}</p>
                )}

              <div className="save-product-section">
              <Heart 
                className={`save-icon ${isSaved ? 'saved' : ''}`} 
                onClick={handleSaveItem}
                title={isSaved ? 'Remove from Saved' : 'Save Product'}
              />
            </div>
              </div>

              {/* Add to Bag Button */}
              {!isEditing && (
                <div className="add-to-bag">
                  <button onClick={addToCart} className="add-to-bag-button">
                    <ShoppingBag className="shopping-bag-icon" />
                    Add to bag
                  </button>
                </div>
              )}

              <div id="success-message" className="success-message hidden">
                Item added to your bag successfully!
              </div>
            </div>
          </div>

          <div className="featured-products-section">
            <FeaturedProducts />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}